import JSZip from 'jszip'
import { PDFDocument } from 'pdf-lib'
import type { CompletionReportItem } from '@cmms/api-client'

export interface BulkExportProgress {
  done: number
  total: number
}

export interface BulkExportFailure {
  item: CompletionReportItem
  error: string
}

export interface BulkExportResult {
  blob: Blob
  succeeded: number
  failed: BulkExportFailure[]
}

/** Group folder for the zip layout — the completion date only, not time
 *  (e.g. "2026-07-29"), so every report resolved on the same day lands
 *  in one folder regardless of exact timestamp. */
function dateFolderName(resolvedAt: string): string {
  return resolvedAt.slice(0, 10)
}

function reportFilename(item: CompletionReportItem): string {
  const code = (item.code ?? item.id).replace(/[^A-Za-z0-9_-]/g, '-')
  return `${item.kind === 'task' ? 'task' : 'work-order'}-${code}.pdf`
}

/** Fetches every item's report as already-rendered PDF bytes (a real
 *  server-side render — see api/src/lib/htmlToPdf.js), tolerating
 *  individual failures (a deleted record, a transient fetch error)
 *  rather than aborting the whole batch — callers surface `failed`
 *  alongside the successful download instead of losing everything to
 *  one bad row. */
async function fetchAll(
  items: CompletionReportItem[],
  fetchPdfBytes: (id: string) => Promise<ArrayBuffer>,
  onProgress?: (p: BulkExportProgress) => void,
): Promise<{ rendered: { item: CompletionReportItem; bytes: ArrayBuffer }[]; failed: BulkExportFailure[] }> {
  const rendered: { item: CompletionReportItem; bytes: ArrayBuffer }[] = []
  const failed: BulkExportFailure[] = []
  let done = 0
  for (const item of items) {
    try {
      const bytes = await fetchPdfBytes(item.id)
      rendered.push({ item, bytes })
    } catch (e) {
      failed.push({ item, error: e instanceof Error ? e.message : 'Failed to render' })
    }
    done += 1
    onProgress?.({ done, total: items.length })
  }
  return { rendered, failed }
}

/** Zip with one folder per completion date, each containing that day's
 *  report PDFs. */
export async function buildCompletionReportsZip(
  items: CompletionReportItem[],
  fetchPdfBytes: (id: string) => Promise<ArrayBuffer>,
  onProgress?: (p: BulkExportProgress) => void,
): Promise<BulkExportResult> {
  const { rendered, failed } = await fetchAll(items, fetchPdfBytes, onProgress)

  const zip = new JSZip()
  for (const { item, bytes } of rendered) {
    zip.folder(dateFolderName(item.resolved_at))?.file(reportFilename(item), bytes)
  }
  const blob = await zip.generateAsync({ type: 'blob' })
  return { blob, succeeded: rendered.length, failed }
}

/** All matching reports merged into a single multi-page PDF, in
 *  completion-date order. */
export async function buildCombinedCompletionReportsPdf(
  items: CompletionReportItem[],
  fetchPdfBytes: (id: string) => Promise<ArrayBuffer>,
  onProgress?: (p: BulkExportProgress) => void,
): Promise<BulkExportResult> {
  const { rendered, failed } = await fetchAll(items, fetchPdfBytes, onProgress)

  const merged = await PDFDocument.create()
  for (const { bytes } of rendered) {
    const src = await PDFDocument.load(bytes)
    const pages = await merged.copyPages(src, src.getPageIndices())
    pages.forEach((p) => merged.addPage(p))
  }
  const mergedBytes = await merged.save()
  return { blob: new Blob([mergedBytes as BlobPart], { type: 'application/pdf' }), succeeded: rendered.length, failed }
}
